import { ActionModal } from '@kcftech/design-system';
import {
    IntegrationResponse,
    IntegrationUpdateRequest,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { updateIntegration } from 'utils/integration';

export type StatusUpdateInfo = Readonly<{
    locationId: string;
    integration: IntegrationResponse;
    affectedIntegration: IntegrationResponse;
}>;

export type StatusConfirmationModalProps = Readonly<{
    updateInfo: StatusUpdateInfo;
    onClose: () => void;
}>;

export function IntegrationStatusConfirmationModal({
    updateInfo: { locationId, integration, affectedIntegration },
    onClose,
}: StatusConfirmationModalProps) {
    const { showToast } = useNotificationContext();
    const { mutate: updateStatus, isPending: isUpdating } = useMutation({
        mutationFn: async () => {
            await updateIntegration({
                locationId,
                integration: affectedIntegration,
                updatedIntegration: new IntegrationUpdateRequest({
                    Active: false,
                    SoftwareName: affectedIntegration.SoftwareName,
                }),
            });
            await updateIntegration({
                locationId,
                integration,
                updatedIntegration: new IntegrationUpdateRequest({
                    Active: true,
                    SoftwareName: integration.SoftwareName,
                }),
            });
        },

        onSuccess: () => {
            showToast({
                type: 'success',
                content: `Successfully activated integration ${integration.SoftwareName} and deactivated ${affectedIntegration.SoftwareName}`,
            });
            onClose();
        },

        onError: async () => {
            showToast({ type: 'error', content: 'Failed to activate integration' });
            // If any error occurs, we want to revert any changes made
            await updateIntegration({
                locationId,
                integration,
                updatedIntegration: new IntegrationUpdateRequest({
                    Active: false,
                    SoftwareName: integration.SoftwareName,
                }),
            });
            await updateIntegration({
                locationId,
                integration: affectedIntegration,
                updatedIntegration: new IntegrationUpdateRequest({
                    Active: true,
                    SoftwareName: affectedIntegration.SoftwareName,
                }),
            });
        },
    });

    return (
        <ActionModal
            title={`Activate ${integration.SoftwareName}`}
            width={700}
            height={350}
            closeButtonText="Cancel"
            actionButtonText="Confirm"
            onAction={updateStatus}
            onClose={onClose}
            showActionButton
            actionable={!isUpdating}
            closable={!isUpdating}
        >
            {`Only one ${integration.IntegrationType.toUpperCase()} integration can be active at a time. Activating ${
                integration.SoftwareName
            } will deactivate ${affectedIntegration.SoftwareName}.`}
        </ActionModal>
    );
}
